import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SignOut, Trash, HandsPraying, Eye, UserPlus, WhatsappLogo, EnvelopeSimple, Image as ImageIcon, CalendarPlus, UploadSimple, BookOpenText, Sparkle, House, ShareNetwork, YoutubeLogo, InstagramLogo, FacebookLogo, TiktokLogo, CheckCircle, Copy, ArrowSquareOut, DownloadSimple, MusicNote, SpotifyLogo, Books } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const api = axios.create({ withCredentials: true });

const estadoMeta = {
  nuevo: { label: "Nuevo", cls: "border-fire/40 bg-fire/10 text-fire" },
  leido: { label: "Leído", cls: "border-white/15 bg-white/5 text-smoke" },
  orado: { label: "Orado", cls: "border-emerald-400/25 bg-emerald-400/10 text-emerald-300" },
};

const inputCls =
  "w-full border-b border-white/15 bg-transparent py-3 text-sm font-light tracking-wide text-paper placeholder:text-paper/35 transition-colors duration-300 focus:border-fire";

const waNumber = (t) => {
  const d = (t || "").replace(/\D/g, "");
  return d.length === 10 ? `52${d}` : d;
};

const NAV = [
  { v: "mensajes", l: "Mensajes", icon: EnvelopeSimple },
  { v: "eventos", l: "Eventos", icon: CalendarPlus },
  { v: "galeria", l: "Galería", icon: ImageIcon },
  { v: "radio", l: "Radio", icon: MusicNote },
  { v: "libreria", l: "Librería", icon: Books },
  { v: "versiculos", l: "Versículos", icon: BookOpenText },
  { v: "contenido", l: "Contenido IA", icon: Sparkle },
  { v: "redes", l: "Redes", icon: ShareNetwork },
  { v: "equipo", l: "Equipo", icon: UserPlus },
];

const NET_META = {
  instagram: { name: "Instagram", icon: InstagramLogo, open: "https://www.instagram.com/" },
  facebook: { name: "Facebook", icon: FacebookLogo, open: "https://www.facebook.com/" },
  tiktok: { name: "TikTok", icon: TiktokLogo, open: "https://www.tiktok.com/upload" },
  youtube: { name: "YouTube", icon: YoutubeLogo, open: "https://studio.youtube.com/" },
};

const FIELD_LABELS = { texto: "Texto", hashtags: "Hashtags", guion: "Guion", caption: "Caption", titulo: "Título", descripcion: "Descripción" };

const TIPOS = [
  { v: "post", l: "Publicación con foto" },
  { v: "reel", l: "Guion Reel / TikTok" },
  { v: "youtube", l: "Título + descripción YouTube" },
  { v: "invitacion", l: "Invitación a evento" },
];

const Card = ({ children, className = "" }) => (
  <div className={`border border-white/10 bg-surface ${className}`}>{children}</div>
);

const AdminPanel = () => {
  const [session, setSession] = useState(undefined);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("mensajes");

  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("todas");

  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ nombre: "", email: "", password: "" });

  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ titulo: "", fecha: "", hora: "", lugar: "", descripcion: "" });
  const [expandedEvent, setExpandedEvent] = useState(null);

  const [gallery, setGallery] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [verses, setVerses] = useState([]);
  const [newVerse, setNewVerse] = useState({ texto: "", referencia: "" });
  const [tema, setTema] = useState("");
  const [generating, setGenerating] = useState(false);

  const [socials, setSocials] = useState({ youtube: "", instagram: "", facebook: "", tiktok: "", spotify: "" });
  const [savingSocials, setSavingSocials] = useState(false);

  const [idea, setIdea] = useState("");
  const [tipo, setTipo] = useState("post");
  const [redesSel, setRedesSel] = useState(["instagram", "facebook"]);
  const [genContent, setGenContent] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [imgPrompt, setImgPrompt] = useState("");
  const [genImgBusy, setGenImgBusy] = useState(false);
  const [genImage, setGenImage] = useState(null);
  const [publishingImg, setPublishingImg] = useState(false);

  const [tracks, setTracks] = useState([]);
  const [trackForm, setTrackForm] = useState({ titulo: "", artista: "Viento de Gracia" });
  const [uploadingTrack, setUploadingTrack] = useState(false);
  const trackFileRef = useRef(null);

  const [books, setBooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const emptyBook = { titulo: "", subtitulo: "", autor: "", precio: "", precio_oferta: "", descripcion: "", tags: "", isbn: "", stock: "1", destacado: false, resena: "" };
  const [bookForm, setBookForm] = useState(emptyBook);
  const [savingBook, setSavingBook] = useState(false);
  const bookFileRef = useRef(null);
  const [resenaOpen, setResenaOpen] = useState(null);
  const [resenaDraft, setResenaDraft] = useState("");
  const [precioEdit, setPrecioEdit] = useState(null);
  const [precioDraft, setPrecioDraft] = useState("");
  const [fotosOpen, setFotosOpen] = useState(null);
  const [uploadingFotos, setUploadingFotos] = useState(false);
  const fotosFileRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [m, u, e, g, v, st, c, r, b, o] = await Promise.all([
        api.get(`${API}/messages`),
        api.get(`${API}/auth/users`),
        api.get(`${API}/admin/events`),
        api.get(`${API}/gallery`),
        api.get(`${API}/verses`),
        api.get(`${API}/settings`),
        api.get(`${API}/content`),
        api.get(`${API}/radio/tracks`),
        api.get(`${API}/books`),
        api.get(`${API}/libreria/orders`),
      ]);
      setMessages(m.data);
      setUsers(u.data);
      setEvents(e.data);
      setGallery(g.data);
      setVerses(v.data);
      setSocials(st.data.socials || { youtube: "", instagram: "", facebook: "", tiktok: "", spotify: "" });
      setHistory(c.data);
      setTracks(r.data);
      setBooks(b.data);
      setOrders(o.data);
    } catch {
      setSession(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`${API}/auth/me`);
        setSession(data);
      } catch {
        setSession(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (session) loadAll();
  }, [session, loadAll]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await api.post(`${API}/auth/login`, loginForm);
      setSession(data);
      toast.success(`Bienvenido, ${data.nombre || data.email}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "No pudimos iniciar sesión");
    }
  };

  const doLogout = async () => {
    await api.post(`${API}/auth/logout`);
    setSession(null);
  };

  const setEstado = async (id, estado) => {
    try {
      const { data } = await api.patch(`${API}/messages/${id}`, { estado });
      setMessages((m) => m.map((x) => (x.id === id ? data : x)));
      toast.success(estado === "orado" ? "Marcado como orado" : "Marcado como leído");
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const removeMessage = async (id) => {
    try {
      await api.delete(`${API}/messages/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Mensaje eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${API}/auth/users`, newUser);
      toast.success(`${newUser.nombre} agregado al equipo`);
      setNewUser({ nombre: "", email: "", password: "" });
      const { data } = await api.get(`${API}/auth/users`);
      setUsers(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo agregar");
    }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${API}/events`, newEvent);
      toast.success("Evento publicado en el sitio");
      setNewEvent({ titulo: "", fecha: "", hora: "", lugar: "", descripcion: "" });
      const { data } = await api.get(`${API}/admin/events`);
      setEvents(data);
    } catch {
      toast.error("No se pudo crear el evento");
    }
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`${API}/events/${id}`);
      setEvents((ev) => ev.filter((x) => x.id !== id));
      toast.success("Evento eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const uploadPhoto = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Elige una imagen primero");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post(`${API}/gallery`, fd);
      toast.success("Foto publicada en la galería");
      setCaption("");
      fileRef.current.value = "";
      const { data } = await api.get(`${API}/gallery`);
      setGallery(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo subir la foto");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`${API}/gallery/${id}`);
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const canMusic = session && (session.role === "admin" || users.some((u) => u.id === session.id && u.puede_musica));

  const uploadTrack = async (e) => {
    e.preventDefault();
    const file = trackFileRef.current?.files?.[0];
    if (!file) {
      toast.error("Elige un archivo de audio primero");
      return;
    }
    setUploadingTrack(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("titulo", trackForm.titulo);
      fd.append("artista", trackForm.artista || "Viento de Gracia");
      await api.post(`${API}/radio/tracks`, fd);
      toast.success("Canción publicada en la radio");
      setTrackForm({ titulo: "", artista: "Viento de Gracia" });
      trackFileRef.current.value = "";
      const { data } = await api.get(`${API}/radio/tracks`);
      setTracks(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo subir la canción");
    } finally {
      setUploadingTrack(false);
    }
  };

  const removeTrack = async (id) => {
    try {
      await api.delete(`${API}/radio/tracks/${id}`);
      setTracks((t) => t.filter((x) => x.id !== id));
      toast.success("Canción eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const toggleMusic = async (u) => {
    try {
      await api.patch(`${API}/auth/users/${u.id}`, { puede_musica: !u.puede_musica });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, puede_musica: !u.puede_musica } : x)));
      toast.success(u.puede_musica ? `Permiso de radio quitado a ${u.nombre}` : `${u.nombre} ya puede subir música`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo actualizar el permiso");
    }
  };

  const publishImageToGallery = async () => {
    if (!genImage) return;
    setPublishingImg(true);
    try {
      await api.post(`${API}/gallery/from-content`, { image_id: genImage.id, caption: genImage.prompt.slice(0, 60) });
      const { data } = await api.get(`${API}/gallery`);
      setGallery(data);
      toast.success("Imagen publicada en la galería del sitio");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo publicar en la galería");
    } finally {
      setPublishingImg(false);
    }
  };

  const addBook = async (e) => {
    e.preventDefault();
    const file = bookFileRef.current?.files?.[0];
    if (!file) {
      toast.error("Sube la portada del libro");
      return;
    }
    if (!bookForm.precio || Number(bookForm.precio) <= 0) {
      toast.error("Escribe un precio válido");
      return;
    }
    setSavingBook(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries({ ...bookForm, stock: bookForm.stock || "1" }).forEach(([k, v]) => fd.append(k, String(v)));
      await api.post(`${API}/books`, fd);
      toast.success("Libro publicado en la librería");
      setBookForm(emptyBook);
      bookFileRef.current.value = "";
      const { data } = await api.get(`${API}/books`);
      setBooks(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo publicar el libro");
    } finally {
      setSavingBook(false);
    }
  };

  const removeBook = async (id) => {
    try {
      await api.delete(`${API}/books/${id}`);
      setBooks((b) => b.filter((x) => x.id !== id));
      toast.success("Libro eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const patchBook = async (id, cambios) => {
    try {
      await api.patch(`${API}/books/${id}`, cambios);
      setBooks((b) => b.map((x) => (x.id === id ? { ...x, ...cambios } : x)));
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const markOrder = async (id, estado) => {
    try {
      await api.patch(`${API}/libreria/orders/${id}`, { estado });
      setOrders((o) => o.map((x) => (x.id === id ? { ...x, status: estado } : x)));
      toast.success(estado === "entregado" ? "Pedido marcado como entregado" : "Pedido cancelado");
    } catch {
      toast.error("No se pudo actualizar el pedido");
    }
  };

  const uploadBookFotos = async (id) => {
    const files = fotosFileRef.current?.files;
    if (!files || files.length === 0) {
      toast.error("Elige una o más fotos primero");
      return;
    }
    setUploadingFotos(true);
    try {
      const fd = new FormData();
      [...files].forEach((f) => fd.append("files", f));
      const { data } = await api.post(`${API}/books/${id}/fotos`, fd);
      setBooks((b) => b.map((x) => (x.id === id ? data : x)));
      fotosFileRef.current.value = "";
      toast.success("Fotos agregadas al libro");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudieron subir las fotos");
    } finally {
      setUploadingFotos(false);
    }
  };

  const removeBookFoto = async (id, path) => {
    try {
      await api.delete(`${API}/books/${id}/fotos`, { data: { path } });
      setBooks((b) => b.map((x) => (x.id === id ? { ...x, fotos: x.fotos.filter((f) => f.path !== path) } : x)));
      toast.success("Foto eliminada");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo eliminar la foto");
    }
  };

  const addVerse = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${API}/verses`, newVerse);
      toast.success("Versículo agregado a la rotación semanal");
      setNewVerse({ texto: "", referencia: "" });
      setTema("");
      const { data } = await api.get(`${API}/verses`);
      setVerses(data);
    } catch {
      toast.error("No se pudo agregar");
    }
  };

  const removeVerse = async (id) => {
    try {
      await api.delete(`${API}/verses/${id}`);
      setVerses((v) => v.filter((x) => x.id !== id));
      toast.success("Versículo eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const generateVerse = async () => {
    if (!tema.trim()) {
      toast.error("Escribe un tema o idea primero");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`${API}/verses/generate`, { tema });
      setNewVerse({ texto: data.texto, referencia: data.referencia });
      toast.success("Versículo sugerido — revísalo antes de agregar");
    } catch {
      toast.error("No se pudo generar. Intenta de nuevo.");
    } finally {
      setGenerating(false);
    }
  };

  const saveSocials = async (e) => {
    e.preventDefault();
    setSavingSocials(true);
    try {
      const { data } = await api.put(`${API}/settings/socials`, socials);
      setSocials(data.socials);
      toast.success("Redes actualizadas — el sitio ya las muestra");
    } catch {
      toast.error("No se pudieron guardar");
    } finally {
      setSavingSocials(false);
    }
  };

  const toggleRed = (key) =>
    setRedesSel((r) => (r.includes(key) ? r.filter((x) => x !== key) : [...r, key]));

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado — pégalo en la red social");
  };

  const generateContent = async () => {
    if (!idea.trim()) {
      toast.error("Escribe tu idea primero");
      return;
    }
    if (redesSel.length === 0) {
      toast.error("Elige al menos una red social");
      return;
    }
    setGenBusy(true);
    try {
      const { data } = await api.post(`${API}/content/generate`, { idea, tipo, redes: redesSel });
      setGenContent(data.resultado);
      const { data: h } = await api.get(`${API}/content`);
      setHistory(h);
      toast.success("Contenido generado — revísalo y cópialo");
    } catch {
      toast.error("No se pudo generar. Intenta de nuevo.");
    } finally {
      setGenBusy(false);
    }
  };

  const removeContent = async (id) => {
    try {
      await api.delete(`${API}/content/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("Eliminado del historial");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const generateImage = async () => {
    const prompt = imgPrompt.trim() || idea.trim();
    if (!prompt) {
      toast.error("Describe la imagen o escribe una idea arriba");
      return;
    }
    setGenImgBusy(true);
    setGenImage(null);
    try {
      const { data } = await api.post(`${API}/content/image`, { prompt }, { timeout: 120000 });
      setGenImage(data);
      toast.success("Imagen lista — descárgala y úsala en tu post");
    } catch {
      toast.error("No se pudo generar la imagen. Intenta de nuevo.");
    } finally {
      setGenImgBusy(false);
    }
  };

  if (session === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-paper" data-testid="panel-loading">
        <img src="/logo-mark-oficial.png" alt="Cargando" className="h-12 w-12 animate-float-slow object-contain" />
      </div>
    );
  }

  if (session === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6 text-paper" data-testid="panel-login-screen">
        <div className="w-full max-w-md border border-white/10 bg-surface p-10">
          <div className="mb-10 flex items-center gap-3">
            <img src="/logo-mark-oficial.png" alt="Viento de Gracia" className="h-10 w-10 object-contain" />
            <span className="font-serif text-lg uppercase tracking-[0.2em]">Viento de <em className="italic text-gold">Gracia</em></span>
          </div>
          <h1 className="font-serif text-3xl" data-testid="panel-login-title">Panel pastoral</h1>
          <p className="mt-2 text-sm font-light text-smoke">Acceso exclusivo para el equipo pastoral.</p>
          <form onSubmit={doLogin} className="mt-8 space-y-6" data-testid="panel-login-form">
            <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} placeholder="Correo electrónico" className={inputCls} data-testid="panel-input-email" />
            <input type="password" required value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} placeholder="Contraseña" className={inputCls} data-testid="panel-input-password" />
            {loginError && <p className="text-sm text-red-400" data-testid="panel-login-error">{loginError}</p>}
            <button type="submit" data-testid="panel-login-submit" className="w-full bg-paper py-4 text-[11px] font-medium uppercase tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper">
              Entrar
            </button>
          </form>
          <a href="/" className="mt-8 block text-center text-[10px] font-light uppercase tracking-[0.25em] text-smoke transition-colors hover:text-fire" data-testid="panel-back-home">
            ← Volver al sitio
          </a>
        </div>
      </div>
    );
  }

  const filtered = messages.filter((m) => filter === "todas" || m.tipo === filter);
  const nuevas = messages.filter((m) => m.estado === "nuevo").length;
  const oradas = messages.filter((m) => m.estado === "orado").length;

  return (
    <div className="flex min-h-screen bg-ink text-paper" data-testid="panel-dashboard">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-white/10 bg-surface/50 lg:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-6">
          <img src="/logo-mark-oficial.png" alt="Viento de Gracia" className="h-9 w-9 object-contain" />
          <div>
            <p className="font-serif text-sm uppercase tracking-[0.2em]">Viento de <em className="italic text-gold">Gracia</em></p>
            <p className="text-[9px] font-light uppercase tracking-[0.3em] text-smoke">Panel pastoral</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-4" data-testid="panel-tabs">
          {NAV.map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`panel-tab-${t.v}`}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left text-[11px] font-light uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === t.v ? "bg-fire/10 text-fire" : "text-paper/60 hover:bg-white/5 hover:text-paper"
              }`}
            >
              <t.icon size={17} weight="light" /> {t.l}
              {t.v === "mensajes" && nuevas > 0 && (
                <span className="ml-auto bg-fire px-2 py-0.5 text-[9px] font-medium text-ink" data-testid="panel-badge-nuevas">{nuevas}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <p className="truncate px-2 text-xs font-light text-smoke" data-testid="panel-user-name">{session.nombre || session.email}</p>
          <div className="mt-3 space-y-1">
            <a href="/" className="flex w-full items-center gap-3 px-2 py-2 text-[10px] font-light uppercase tracking-[0.2em] text-paper/50 transition-colors hover:text-paper" data-testid="panel-home-link">
              <House size={15} weight="light" /> Ver sitio
            </a>
            <button onClick={doLogout} data-testid="panel-logout-btn" className="flex w-full items-center gap-3 px-2 py-2 text-[10px] font-light uppercase tracking-[0.2em] text-paper/50 transition-colors hover:text-fire">
              <SignOut size={15} weight="light" /> Salir
            </button>
          </div>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="flex items-center justify-between border-b border-white/10 px-6 py-4 lg:hidden">
          <div className="flex items-center gap-2">
            <img src="/logo-mark-oficial.png" alt="" className="h-7 w-7 object-contain" />
            <span className="font-serif text-xs uppercase tracking-[0.2em]">Panel pastoral</span>
          </div>
          <button onClick={doLogout} data-testid="panel-logout-mobile" className="text-[10px] font-light uppercase tracking-[0.2em] text-paper/60">Salir</button>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-white/10 px-4 py-3 lg:hidden" data-testid="panel-tabs-mobile">
          {NAV.map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`panel-tab-mobile-${t.v}`}
              className={`flex shrink-0 items-center gap-2 px-4 py-2 text-[10px] font-light uppercase tracking-[0.2em] transition-colors ${tab === t.v ? "bg-fire/10 text-fire" : "text-paper/60"}`}
            >
              <t.icon size={15} weight="light" /> {t.l}
            </button>
          ))}
        </nav>

        <main className="mx-auto max-w-5xl px-6 py-10 lg:px-10">
          {tab === "mensajes" && (
            <>
              <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
                <div>
                  <h1 className="font-serif text-4xl" data-testid="panel-title">Mensajes y peticiones</h1>
                  <p className="mt-2 text-sm font-light text-smoke">Lo que llega desde el formulario del sitio.</p>
                </div>
                <div className="flex gap-2" data-testid="panel-filters">
                  {[
                    { v: "todas", l: "Todas" },
                    { v: "oracion", l: "Oración" },
                    { v: "contacto", l: "Contacto" },
                  ].map((f) => (
                    <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`panel-filter-${f.v}`} className={`border px-4 py-2 text-[10px] font-light uppercase tracking-[0.2em] transition-colors duration-300 ${filter === f.v ? "border-paper bg-paper text-ink" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}>
                      {f.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-8 grid grid-cols-3 border border-white/10" data-testid="panel-stats">
                {[
                  { v: nuevas, l: "Nuevas", hl: true },
                  { v: oradas, l: "Oradas", hl: false },
                  { v: messages.length, l: "Total", hl: false },
                ].map((s, i) => (
                  <div key={s.l} className={`px-6 py-5 ${i > 0 ? "border-l border-white/10" : ""}`}>
                    <p className={`font-serif text-4xl ${s.hl ? "text-fire" : "text-paper"}`}>{s.v}</p>
                    <p className="mt-1 text-[9px] font-light uppercase tracking-[0.3em] text-smoke">{s.l}</p>
                  </div>
                ))}
              </div>

              {filtered.length === 0 ? (
                <p className="border border-dashed border-white/15 py-20 text-center font-serif text-2xl italic text-smoke" data-testid="panel-empty">
                  Aún no hay mensajes aquí
                </p>
              ) : (
                <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2" data-testid="panel-messages-grid">
                  {filtered.map((m) => (
                    <article key={m.id} className="bg-surface p-7 transition-colors duration-300 hover:bg-elevated" data-testid={`panel-msg-${m.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-serif text-xl">{m.nombre}</h3>
                          <p className="mt-1 text-xs font-light text-smoke">
                            {m.email || "sin correo"} {m.telefono && `· ${m.telefono}`}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`border px-3 py-1 text-[9px] font-light uppercase tracking-[0.15em] ${estadoMeta[m.estado]?.cls}`} data-testid={`panel-msg-estado-${m.id}`}>
                            {estadoMeta[m.estado]?.label}
                          </span>
                          <span className="text-[9px] font-light uppercase tracking-[0.15em] text-smoke/70">
                            {m.tipo === "oracion" ? "Oración" : "Contacto"} · {m.created_at.slice(0, 10)}
                          </span>
                        </div>
                      </div>
                      <p className="mt-5 border-l border-fire/50 pl-4 text-sm font-light leading-relaxed text-paper/85">{m.mensaje}</p>
                      <div className="mt-6 flex flex-wrap gap-2">
                        {m.estado === "nuevo" && (
                          <button onClick={() => setEstado(m.id, "leido")} data-testid={`panel-msg-leido-${m.id}`} className="flex items-center gap-1.5 border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-paper hover:text-paper">
                            <Eye size={13} weight="light" /> Leído
                          </button>
                        )}
                        {m.estado !== "orado" && (
                          <button onClick={() => setEstado(m.id, "orado")} data-testid={`panel-msg-orado-${m.id}`} className="flex items-center gap-1.5 border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-fire hover:text-fire">
                            <HandsPraying size={13} weight="light" /> Orado
                          </button>
                        )}
                        {m.telefono && (
                          <a
                            href={`https://wa.me/${waNumber(m.telefono)}?text=${encodeURIComponent(`Hola ${m.nombre}, te saluda el equipo pastoral de la Iglesia Viento de Gracia. Recibimos tu ${m.tipo === "oracion" ? "petición de oración" : "mensaje"} y queremos darte seguimiento.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`panel-msg-wa-${m.id}`}
                            className="flex items-center gap-1.5 border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-emerald-400/50 hover:text-emerald-300"
                          >
                            <WhatsappLogo size={13} weight="light" /> WhatsApp
                          </a>
                        )}
                        {m.email && (
                          <a
                            href={`mailto:${m.email}?subject=${encodeURIComponent("Viento de Gracia — respuesta a tu mensaje")}&body=${encodeURIComponent(`Hola ${m.nombre},\n\nTe saluda el equipo pastoral de la Iglesia Viento de Gracia. Recibimos tu mensaje:\n\n"${m.mensaje}"\n\n`)}`}
                            data-testid={`panel-msg-mail-${m.id}`}
                            className="flex items-center gap-1.5 border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-fire hover:text-fire"
                          >
                            <EnvelopeSimple size={13} weight="light" /> Correo
                          </a>
                        )}
                        <button onClick={() => removeMessage(m.id)} data-testid={`panel-msg-delete-${m.id}`} className="ml-auto flex items-center gap-1.5 border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-red-400/50 hover:text-red-400">
                          <Trash size={13} weight="light" /> Eliminar
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "eventos" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-eventos-title">Eventos especiales</h1>
              <p className="mt-2 text-sm font-light text-smoke">Los eventos publicados aparecen en la sección "Eventos" del sitio con registro abierto.</p>

              <Card className="mt-8 p-8" data-testid="panel-event-form">
                <form onSubmit={addEvent}>
                  <h2 className="flex items-center gap-2 font-serif text-2xl">
                    <CalendarPlus size={20} weight="light" className="text-fire" /> Nuevo evento
                  </h2>
                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    <input required value={newEvent.titulo} onChange={(e) => setNewEvent((f) => ({ ...f, titulo: e.target.value }))} placeholder="Título del evento *" className={inputCls} data-testid="panel-event-titulo" />
                    <input required type="date" value={newEvent.fecha} onChange={(e) => setNewEvent((f) => ({ ...f, fecha: e.target.value }))} className={inputCls} data-testid="panel-event-fecha" />
                    <input value={newEvent.hora} onChange={(e) => setNewEvent((f) => ({ ...f, hora: e.target.value }))} placeholder="Hora (ej: 7:00 PM)" className={inputCls} data-testid="panel-event-hora" />
                    <input value={newEvent.lugar} onChange={(e) => setNewEvent((f) => ({ ...f, lugar: e.target.value }))} placeholder="Lugar (ej: Templo principal)" className={inputCls} data-testid="panel-event-lugar" />
                  </div>
                  <textarea rows={3} value={newEvent.descripcion} onChange={(e) => setNewEvent((f) => ({ ...f, descripcion: e.target.value }))} placeholder="Descripción del evento" className={`${inputCls} mt-5 resize-none`} data-testid="panel-event-descripcion" />
                  <button type="submit" data-testid="panel-event-submit" className="mt-6 bg-paper px-8 py-3 text-[10px] font-medium uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper">
                    Publicar evento
                  </button>
                </form>
              </Card>

              <div className="mt-8 space-y-px border border-white/10 bg-white/10" data-testid="panel-events-list">
                {events.length === 0 && (
                  <p className="bg-surface py-16 text-center font-serif text-xl italic text-smoke" data-testid="panel-events-empty">
                    No hay eventos publicados
                  </p>
                )}
                {events.map((ev) => (
                  <article key={ev.id} className="bg-surface p-7" data-testid={`panel-event-${ev.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-2xl">{ev.titulo}</h3>
                        <p className="mt-1 text-[10px] font-light uppercase tracking-[0.25em] text-smoke">
                          {ev.fecha} {ev.hora && `· ${ev.hora}`} {ev.lugar && `· ${ev.lugar}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedEvent((x) => (x === ev.id ? null : ev.id))} data-testid={`panel-event-inscritos-${ev.id}`} className="border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-paper hover:text-paper">
                          {ev.inscritos.length} inscritos
                        </button>
                        <button onClick={() => removeEvent(ev.id)} data-testid={`panel-event-delete-${ev.id}`} className="flex items-center gap-1.5 border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-red-400/50 hover:text-red-400">
                          <Trash size={13} weight="light" /> Eliminar
                        </button>
                      </div>
                    </div>
                    {expandedEvent === ev.id && (
                      <div className="mt-5 border-t border-white/10 pt-5" data-testid={`panel-event-inscritos-list-${ev.id}`}>
                        {ev.inscritos.length === 0 ? (
                          <p className="text-sm font-light italic text-smoke">Aún no hay registros.</p>
                        ) : (
                          <ul className="space-y-2">
                            {ev.inscritos.map((r) => (
                              <li key={r.id} className="text-sm font-light text-paper/85" data-testid={`panel-inscrito-${r.id}`}>
                                <span className="font-normal">{r.nombre}</span>
                                <span className="text-smoke"> · {r.email || "sin correo"} {r.telefono && `· ${r.telefono}`}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}

          {tab === "galeria" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-galeria-title">Galería del sitio</h1>
              <p className="mt-2 text-sm font-light text-smoke">Sube fotos reales de la congregación: aparecen de inmediato en "Momentos de encuentro".</p>

              <Card className="mt-8 p-8" data-testid="panel-gallery-form">
                <form onSubmit={uploadPhoto}>
                  <h2 className="flex items-center gap-2 font-serif text-2xl">
                    <ImageIcon size={20} weight="light" className="text-fire" /> Subir foto
                  </h2>
                  <div className="mt-6 grid gap-5 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="text-sm font-light text-smoke file:mr-4 file:border file:border-white/15 file:bg-ink file:px-5 file:py-2 file:text-[10px] file:font-light file:uppercase file:tracking-[0.15em] file:text-paper hover:file:border-fire" data-testid="panel-gallery-file" />
                    <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Pie de foto (ej: Bautizos 2026)" className={inputCls} data-testid="panel-gallery-caption" />
                    <button type="submit" disabled={uploading} data-testid="panel-gallery-submit" className="flex items-center justify-center gap-2 bg-paper px-7 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper disabled:opacity-60">
                      <UploadSimple size={14} weight="light" /> {uploading ? "Subiendo..." : "Subir"}
                    </button>
                  </div>
                </form>
              </Card>

              <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3" data-testid="panel-gallery-grid">
                {gallery.length === 0 && (
                  <p className="col-span-full bg-surface py-16 text-center font-serif text-xl italic text-smoke" data-testid="panel-gallery-empty">
                    No hay fotos subidas — el sitio muestra imágenes de muestra
                  </p>
                )}
                {gallery.map((p) => (
                  <figure key={p.id} className="group relative bg-surface" data-testid={`panel-photo-${p.id}`}>
                    <img src={`${BACKEND_URL}${p.url}`} alt={p.caption || "Foto"} className="aspect-[4/3] w-full object-cover" />
                    <figcaption className="flex items-center justify-between gap-2 px-4 py-3">
                      <span className="truncate text-xs font-light text-smoke">{p.caption || "Sin pie de foto"}</span>
                      <button onClick={() => removePhoto(p.id)} data-testid={`panel-photo-delete-${p.id}`} className="shrink-0 text-smoke transition-colors hover:text-red-400">
                        <Trash size={15} weight="light" />
                      </button>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </>
          )}

          {tab === "radio" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-radio-title">Radio Viento de Gracia</h1>
              <p className="mt-2 text-sm font-light text-smoke">
                Sube las canciones de la casa (MP3, WAV, OGG o M4A, máx 20 MB). Suenan al instante en la página de radio del sitio.
              </p>

              {!canMusic && (
                <Card className="mt-8 border-fire/30 p-8" data-testid="panel-radio-noperm">
                  <p className="text-sm font-light text-smoke">
                    Tu cuenta aún no tiene permiso para subir música. Pídele al administrador que lo active en la pestaña <span className="text-paper">Equipo</span>.
                  </p>
                </Card>
              )}

              {canMusic && (
                <Card className="mt-8 p-8" data-testid="panel-radio-form">
                  <h2 className="flex items-center gap-2 font-serif text-2xl">
                    <MusicNote size={20} weight="light" className="text-fire" /> Subir canción
                  </h2>
                  <form onSubmit={uploadTrack} className="mt-6 space-y-5">
                    <input ref={trackFileRef} type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/x-m4a,audio/m4a" className="text-sm font-light text-smoke file:mr-4 file:border file:border-white/15 file:bg-ink file:px-5 file:py-2 file:text-[10px] file:font-light file:uppercase file:tracking-[0.15em] file:text-paper hover:file:border-fire" data-testid="panel-radio-file" />
                    <div className="grid gap-5 sm:grid-cols-2">
                      <input required value={trackForm.titulo} onChange={(e) => setTrackForm((f) => ({ ...f, titulo: e.target.value }))} placeholder="Título de la canción *" className={inputCls} data-testid="panel-radio-titulo" />
                      <input value={trackForm.artista} onChange={(e) => setTrackForm((f) => ({ ...f, artista: e.target.value }))} placeholder="Artista (ej: Viento de Gracia)" className={inputCls} data-testid="panel-radio-artista" />
                    </div>
                    <button type="submit" disabled={uploadingTrack} data-testid="panel-radio-submit" className="flex items-center justify-center gap-2 bg-paper px-7 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper disabled:opacity-60">
                      <UploadSimple size={14} weight="light" /> {uploadingTrack ? "Subiendo..." : "Subir a la radio"}
                    </button>
                  </form>
                </Card>
              )}

              <div className="mt-8 space-y-px border border-white/10 bg-white/10" data-testid="panel-radio-list">
                {tracks.length === 0 && (
                  <p className="bg-surface py-16 text-center font-serif text-xl italic text-smoke" data-testid="panel-radio-empty">
                    Aún no hay canciones — sube la primera
                  </p>
                )}
                {tracks.map((t, i) => (
                  <div key={t.id} className="flex items-center gap-5 bg-surface px-6 py-4" data-testid={`panel-track-${t.id}`}>
                    <span className="font-serif text-xl text-smoke">{String(i + 1).padStart(2, "0")}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-serif text-lg">{t.titulo}</p>
                      <p className="text-xs font-light text-smoke">{t.artista} · {t.created_at.slice(0, 10)}</p>
                    </div>
                    <a href="/radio" target="_blank" rel="noopener noreferrer" className="hidden text-[9px] font-light uppercase tracking-[0.2em] text-smoke transition-colors hover:text-fire sm:block" data-testid={`panel-track-escuchar-${t.id}`}>
                      Escuchar en el sitio
                    </a>
                    {canMusic && (
                      <button onClick={() => removeTrack(t.id)} data-testid={`panel-track-delete-${t.id}`} className="shrink-0 text-smoke transition-colors hover:text-red-400">
                        <Trash size={15} weight="light" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "libreria" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-libreria-title">Librería</h1>
              <p className="mt-2 text-sm font-light text-smoke">
                Publica los libros a la venta. Aparecen al instante en la página de librería, con pago con tarjeta y pedido por WhatsApp. Cada libro tiene su propia página lista para Google y Google Shopping.
              </p>

              <Card className="mt-8 p-8" data-testid="panel-book-form">
                <h2 className="flex items-center gap-2 font-serif text-2xl">
                  <Books size={20} weight="light" className="text-fire" /> Agregar libro
                </h2>
                <form onSubmit={addBook} className="mt-6 space-y-5">
                  <input ref={bookFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="text-sm font-light text-smoke file:mr-4 file:border file:border-white/15 file:bg-ink file:px-5 file:py-2 file:text-[10px] file:font-light file:uppercase file:tracking-[0.15em] file:text-paper hover:file:border-fire" data-testid="panel-book-file" />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <input required value={bookForm.titulo} onChange={(e) => setBookForm((f) => ({ ...f, titulo: e.target.value }))} placeholder="Título del libro *" className={inputCls} data-testid="panel-book-titulo" />
                    <input value={bookForm.subtitulo} onChange={(e) => setBookForm((f) => ({ ...f, subtitulo: e.target.value }))} placeholder="Subtítulo (aparece en la portada, ej: Cambia tus pensamientos...)" className={inputCls} data-testid="panel-book-subtitulo" />
                    <input value={bookForm.autor} onChange={(e) => setBookForm((f) => ({ ...f, autor: e.target.value }))} placeholder="Autor" className={inputCls} data-testid="panel-book-autor" />
                    <input type="number" min="0" step="1" value={bookForm.stock} onChange={(e) => setBookForm((f) => ({ ...f, stock: e.target.value }))} placeholder="Existencias (ej: 5)" className={inputCls} data-testid="panel-book-stock" />
                    <input required type="number" min="1" step="0.01" value={bookForm.precio} onChange={(e) => setBookForm((f) => ({ ...f, precio: e.target.value }))} placeholder="Precio normal MXN * (ej: 350)" className={inputCls} data-testid="panel-book-precio" />
                    <input type="number" min="0" step="0.01" value={bookForm.precio_oferta} onChange={(e) => setBookForm((f) => ({ ...f, precio_oferta: e.target.value }))} placeholder="Precio de oferta (opcional — se tacha el normal)" className={inputCls} data-testid="panel-book-oferta" />
                    <input value={bookForm.tags} onChange={(e) => setBookForm((f) => ({ ...f, tags: e.target.value }))} placeholder="Tags separados por coma (ej: oración, familia, jóvenes)" className={inputCls} data-testid="panel-book-tags" />
                    <input value={bookForm.isbn} onChange={(e) => setBookForm((f) => ({ ...f, isbn: e.target.value }))} placeholder="ISBN (opcional — ayuda a Google Shopping)" className={inputCls} data-testid="panel-book-isbn" />
                  </div>
                  <textarea value={bookForm.descripcion} onChange={(e) => setBookForm((f) => ({ ...f, descripcion: e.target.value }))} placeholder="Descripción del libro (de qué trata, para quién es...)" rows={3} className={`${inputCls} resize-none`} data-testid="panel-book-descripcion" />
                  <textarea value={bookForm.resena} onChange={(e) => setBookForm((f) => ({ ...f, resena: e.target.value }))} placeholder="Reseña pastoral (opcional): por qué lo recomiendas — genera confianza y vende más" rows={2} className={`${inputCls} resize-none`} data-testid="panel-book-resena" />
                  <label className="flex cursor-pointer items-center gap-3 text-xs font-light text-paper/80" data-testid="panel-book-destacado-label">
                    <input type="checkbox" checked={bookForm.destacado} onChange={(e) => setBookForm((f) => ({ ...f, destacado: e.target.checked }))} className="h-4 w-4 accent-[#068176]" data-testid="panel-book-destacado" />
                    Mostrar en los destacados de la página principal
                  </label>
                  <button type="submit" disabled={savingBook} data-testid="panel-book-submit" className="flex items-center justify-center gap-2 bg-paper px-7 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper disabled:opacity-60">
                    <UploadSimple size={14} weight="light" /> {savingBook ? "Publicando..." : "Publicar libro"}
                  </button>
                </form>
              </Card>

              <div className="mt-8 space-y-px border border-white/10 bg-white/10" data-testid="panel-books-list">
                {books.length === 0 && (
                  <p className="bg-surface py-16 text-center font-serif text-xl italic text-smoke" data-testid="panel-books-empty">
                    Aún no hay libros — publica el primero
                  </p>
                )}
                {books.map((b) => (
                  <div key={b.id}>
                    <div className="flex flex-wrap items-center gap-5 bg-surface px-6 py-4" data-testid={`panel-book-${b.id}`}>
                      <img src={`${BACKEND_URL}${b.portada}`} alt={b.titulo} className="h-16 w-12 border border-white/10 object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-serif text-lg">{b.titulo} {b.destacado && <span className="ml-1 text-[9px] uppercase tracking-[0.2em] text-fire">· Destacado</span>}</p>
                        <p className="text-xs font-light text-smoke">
                          {b.autor || "Sin autor"} ·{" "}
                          {precioEdit === b.id ? (
                            <input
                              autoFocus
                              type="number"
                              min="1"
                              step="0.01"
                              value={precioDraft}
                              onChange={(e) => setPrecioDraft(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { patchBook(b.id, { precio: Number(precioDraft) }); setPrecioEdit(null); toast.success("Precio actualizado"); } if (e.key === "Escape") setPrecioEdit(null); }}
                              onBlur={() => { if (precioDraft && Number(precioDraft) !== b.precio) { patchBook(b.id, { precio: Number(precioDraft) }); toast.success("Precio actualizado"); } setPrecioEdit(null); }}
                              className="w-20 border-b border-fire bg-transparent text-xs text-paper focus:outline-none"
                              data-testid={`panel-book-precio-input-${b.id}`}
                            />
                          ) : (
                            <button type="button" onClick={() => { setPrecioEdit(b.id); setPrecioDraft(String(b.precio)); }} title="Clic para editar el precio" className="underline decoration-dotted decoration-white/30 underline-offset-4 transition-colors hover:text-fire" data-testid={`panel-book-precio-${b.id}`}>
                              ${b.precio} MXN{b.precio_oferta > 0 ? ` → oferta $${b.precio_oferta}` : ""}
                            </button>
                          )}{" "}
                          · {(b.tags || []).join(", ") || "sin tags"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2" data-testid={`panel-book-stock-ctl-${b.id}`}>
                        <button onClick={() => patchBook(b.id, { stock: Math.max(0, b.stock - 1) })} className="flex h-8 w-8 items-center justify-center border border-white/15 text-smoke transition-colors hover:border-paper hover:text-paper" aria-label="Menos stock">−</button>
                        <span className={`w-14 text-center text-[10px] font-light uppercase tracking-[0.15em] ${b.stock <= 0 ? "text-red-400" : "text-smoke"}`} data-testid={`panel-book-stock-${b.id}`}>
                          {b.stock <= 0 ? "Agotado" : `${b.stock} pzas`}
                        </span>
                        <button onClick={() => patchBook(b.id, { stock: b.stock + 1 })} className="flex h-8 w-8 items-center justify-center border border-white/15 text-smoke transition-colors hover:border-paper hover:text-paper" aria-label="Más stock">+</button>
                      </div>
                      <button onClick={() => { setFotosOpen(fotosOpen === b.id ? null : b.id); setResenaOpen(null); }} data-testid={`panel-book-fotos-btn-${b.id}`} className={`border px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] transition-colors ${(b.fotos?.length || 0) > 1 ? "border-fire/50 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}>
                        Fotos ({b.fotos?.length || 1})
                      </button>
                      <button onClick={() => { setResenaOpen(resenaOpen === b.id ? null : b.id); setResenaDraft(b.resena || ""); setFotosOpen(null); }} data-testid={`panel-book-resena-btn-${b.id}`} className={`border px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] transition-colors ${b.resena ? "border-fire/50 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}>
                        {b.resena ? "Reseña ✓" : "Reseña"}
                      </button>
                      <button onClick={() => patchBook(b.id, { destacado: !b.destacado })} data-testid={`panel-book-star-${b.id}`} className={`border px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] transition-colors ${b.destacado ? "border-fire/50 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}>
                        {b.destacado ? "Quitar destacado" : "Destacar"}
                      </button>
                      <a href={`/libreria/${b.slug}`} target="_blank" rel="noopener noreferrer" className="hidden text-[9px] font-light uppercase tracking-[0.2em] text-smoke transition-colors hover:text-fire sm:block" data-testid={`panel-book-ver-${b.id}`}>
                        Ver página
                      </a>
                      <button onClick={() => removeBook(b.id)} data-testid={`panel-book-delete-${b.id}`} className="shrink-0 text-smoke transition-colors hover:text-red-400">
                        <Trash size={15} weight="light" />
                      </button>
                    </div>
                    {fotosOpen === b.id && (
                      <div className="border-t border-white/10 bg-ink px-6 py-5" data-testid={`panel-book-fotos-editor-${b.id}`}>
                        <p className="mb-3 text-[10px] font-light uppercase tracking-[0.25em] text-smoke">
                          Fotos del libro — la primera es la portada; las demás se ven como galería en la página del libro
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {(b.fotos || []).map((f) => (
                            <figure key={f.path} className="relative" data-testid={`panel-foto-${f.path.split("/").pop()}`}>
                              <img src={`${BACKEND_URL}${f.url}`} alt="" className={`h-24 w-[72px] border object-cover ${f.portada ? "border-fire" : "border-white/10"}`} />
                              {f.portada ? (
                                <span className="absolute bottom-0 left-0 right-0 bg-fire/90 py-0.5 text-center text-[8px] font-medium uppercase tracking-[0.15em] text-ink">Portada</span>
                              ) : (
                                <button onClick={() => removeBookFoto(b.id, f.path)} data-testid={`panel-foto-delete-${f.path.split("/").pop()}`} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center border border-white/20 bg-ink text-smoke transition-colors hover:text-red-400" aria-label="Quitar foto">
                                  <Trash size={10} weight="light" />
                                </button>
                              )}
                            </figure>
                          ))}
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-4">
                          <input ref={fotosFileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" className="text-sm font-light text-smoke file:mr-4 file:border file:border-white/15 file:bg-ink file:px-5 file:py-2 file:text-[10px] file:font-light file:uppercase file:tracking-[0.15em] file:text-paper hover:file:border-fire" data-testid={`panel-fotos-input-${b.id}`} />
                          <button onClick={() => uploadBookFotos(b.id)} disabled={uploadingFotos} data-testid={`panel-fotos-upload-${b.id}`} className="bg-paper px-6 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-fire hover:text-paper disabled:opacity-60">
                            {uploadingFotos ? "Subiendo..." : "Agregar fotos"}
                          </button>
                        </div>
                      </div>
                    )}
                    {resenaOpen === b.id && (
                      <div className="border-t border-white/10 bg-ink px-6 py-5" data-testid={`panel-book-resena-editor-${b.id}`}>
                        <p className="mb-2 text-[10px] font-light uppercase tracking-[0.25em] text-smoke">Recomendación pastoral — aparece destacada en la página del libro</p>
                        <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
                          <textarea key={b.id} value={resenaDraft} onChange={(e) => setResenaDraft(e.target.value)} placeholder="Ej: «Este libro transformó mi vida de oración; se lo recomiendo a toda la congregación»" rows={2} className={`${inputCls} resize-none`} data-testid={`panel-book-resena-input-${b.id}`} />
                          <button onClick={() => { patchBook(b.id, { resena: resenaDraft }); setResenaOpen(null); toast.success("Reseña guardada"); }} data-testid={`panel-book-resena-save-${b.id}`} className="bg-paper px-6 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-fire hover:text-paper">
                            Guardar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <Card className="mt-10 p-8" data-testid="panel-orders">
                <h2 className="flex items-center gap-2 font-serif text-2xl">
                  <CheckCircle size={20} weight="light" className="text-fire" /> Pedidos con tarjeta
                </h2>
                <p className="mt-2 text-sm font-light text-smoke">
                  Los pedidos pagados aparecen aquí automáticamente. Los pedidos por WhatsApp llegan directo al chat.
                </p>
                <div className="mt-6 space-y-px border border-white/10 bg-white/10" data-testid="panel-orders-list">
                  {orders.length === 0 && (
                    <p className="bg-ink py-12 text-center font-serif text-lg italic text-smoke" data-testid="panel-orders-empty">Sin pedidos todavía</p>
                  )}
                  {orders.map((o) => (
                    <div key={o.id} className="flex flex-wrap items-center gap-4 bg-ink px-6 py-4" data-testid={`panel-order-${o.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-light text-paper/90">
                          {o.titulo} × {o.cantidad} — <span className="text-fire">${o.total} MXN</span>
                        </p>
                        <p className="mt-1 text-xs font-light text-smoke">
                          {o.nombre}{o.telefono ? ` · ${o.telefono}` : ""} · {o.entrega === "domicilio" ? `Envío: ${o.direccion}` : "Recoge en iglesia"} · {o.created_at.slice(0, 10)}
                        </p>
                      </div>
                      <span className={`border px-3 py-1 text-[9px] font-light uppercase tracking-[0.15em] ${o.payment_status === "paid" ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-300" : o.payment_status === "pending" ? "border-fire/40 bg-fire/10 text-fire" : "border-white/15 bg-white/5 text-smoke"}`} data-testid={`panel-order-status-${o.id}`}>
                        {o.payment_status === "paid" ? "Pagado" : o.payment_status === "pending" ? "Pendiente" : o.payment_status}
                      </span>
                      {o.payment_status === "paid" && o.status !== "entregado" && o.status !== "cancelado" && (
                        <button onClick={() => markOrder(o.id, "entregado")} data-testid={`panel-order-entregar-${o.id}`} className="border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-emerald-400/50 hover:text-emerald-300">
                          Marcar entregado
                        </button>
                      )}
                      {o.status === "entregado" && (
                        <span className="text-[9px] font-light uppercase tracking-[0.15em] text-emerald-300" data-testid={`panel-order-entregado-${o.id}`}>Entregado</span>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

          {tab === "versiculos" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-versiculos-title">Versículo de la semana</h1>
              <p className="mt-2 text-sm font-light text-smoke">
                El hero del sitio rota automáticamente entre estos versículos cada semana.
              </p>

              <Card className="mt-8 border-fire/30 p-8" data-testid="panel-verse-ai">
                <h2 className="flex items-center gap-2 font-serif text-2xl">
                  <Sparkle size={20} weight="light" className="text-fire" /> Generar con IA
                </h2>
                <p className="mt-2 text-sm font-light text-smoke">Escribe un tema o idea (ej: "paz en la ansiedad", "gratitud") y la IA sugiere un versículo Reina-Valera que puedes revisar y editar antes de agregar.</p>
                <div className="mt-6 grid gap-5 sm:grid-cols-[1fr_auto] sm:items-end">
                  <input value={tema} onChange={(e) => setTema(e.target.value)} placeholder="Tema o idea para el versículo" className={inputCls} data-testid="panel-verse-tema" />
                  <button type="button" onClick={generateVerse} disabled={generating} data-testid="panel-verse-generate" className="flex items-center justify-center gap-2 bg-fire px-7 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60">
                    <Sparkle size={14} weight="light" /> {generating ? "Generando..." : "Generar con IA"}
                  </button>
                </div>
              </Card>

              <Card className="mt-6 p-8" data-testid="panel-verse-form">
                <form onSubmit={addVerse}>
                  <h2 className="flex items-center gap-2 font-serif text-2xl">
                    <BookOpenText size={20} weight="light" className="text-fire" /> Nuevo versículo
                  </h2>
                  <div className="mt-6 grid gap-5 sm:grid-cols-[1fr_220px_auto] sm:items-end">
                    <input required value={newVerse.texto} onChange={(e) => setNewVerse((f) => ({ ...f, texto: e.target.value }))} placeholder="Texto del versículo *" className={inputCls} data-testid="panel-verse-texto" />
                    <input required value={newVerse.referencia} onChange={(e) => setNewVerse((f) => ({ ...f, referencia: e.target.value }))} placeholder="Referencia (ej: Juan 3:16)" className={inputCls} data-testid="panel-verse-referencia" />
                    <button type="submit" data-testid="panel-verse-submit" className="bg-paper px-7 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper">
                      Agregar
                    </button>
                  </div>
                </form>
              </Card>

              <div className="mt-8 space-y-px border border-white/10 bg-white/10" data-testid="panel-verses-list">
                {verses.length === 0 && (
                  <p className="bg-surface py-16 text-center font-serif text-xl italic text-smoke" data-testid="panel-verses-empty">
                    No hay versículos — el sitio usa el de muestra
                  </p>
                )}
                {verses.map((v) => (
                  <article key={v.id} className="flex items-start justify-between gap-6 bg-surface p-6" data-testid={`panel-verse-${v.id}`}>
                    <div>
                      <p className="text-[10px] font-light uppercase tracking-[0.25em] text-fire">{v.referencia}</p>
                      <p className="mt-2 text-sm font-light leading-relaxed text-paper/85">«{v.texto}»</p>
                    </div>
                    <button onClick={() => removeVerse(v.id)} data-testid={`panel-verse-delete-${v.id}`} className="shrink-0 text-smoke transition-colors hover:text-red-400">
                      <Trash size={16} weight="light" />
                    </button>
                  </article>
                ))}
              </div>
            </>
          )}

          {tab === "contenido" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-contenido-title">Estudio de contenido IA</h1>
              <p className="mt-2 text-sm font-light text-smoke">
                Describe tu idea y la IA escribe el contenido listo para cada red. Revisa, copia y publica.
              </p>

              <Card className="mt-8 border-fire/30 p-8" data-testid="panel-content-studio">
                <textarea
                  rows={3}
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder="¿De qué quieres hablar? Ej: Invita al culto del domingo con enfoque en nuevos comienzos"
                  className={`${inputCls} resize-none !text-base`}
                  data-testid="panel-content-idea"
                />
                <p className="mb-2 mt-7 text-[10px] font-light uppercase tracking-[0.3em] text-smoke">Tipo de pieza</p>
                <div className="flex flex-wrap gap-2" data-testid="panel-content-tipos">
                  {TIPOS.map((t) => (
                    <button
                      key={t.v}
                      type="button"
                      onClick={() => setTipo(t.v)}
                      data-testid={`panel-content-tipo-${t.v}`}
                      className={`border px-4 py-2 text-[10px] font-light uppercase tracking-[0.15em] transition-colors duration-300 ${tipo === t.v ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
                <p className="mb-2 mt-7 text-[10px] font-light uppercase tracking-[0.3em] text-smoke">Publicar en</p>
                <div className="flex flex-wrap gap-2" data-testid="panel-content-redes">
                  {Object.entries(NET_META).map(([key, meta]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleRed(key)}
                      data-testid={`panel-content-red-${key}`}
                      className={`flex items-center gap-2 border px-4 py-2 text-[10px] font-light uppercase tracking-[0.15em] transition-colors duration-300 ${redesSel.includes(key) ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}
                    >
                      <meta.icon size={15} weight="light" /> {meta.name}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={generateContent}
                  disabled={genBusy}
                  data-testid="panel-content-generate"
                  className="mt-8 flex items-center gap-2 bg-fire px-8 py-3.5 text-[10px] font-medium uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60"
                >
                  <Sparkle size={15} weight="light" /> {genBusy ? "Generando..." : "Generar contenido"}
                </button>
              </Card>

              <Card className="mt-6 p-8" data-testid="panel-image-studio">
                <h2 className="flex items-center gap-2 font-serif text-2xl">
                  <ImageIcon size={20} weight="light" className="text-fire" /> Imagen del post con IA
                </h2>
                <p className="mt-2 text-sm font-light text-smoke">
                  Genera la imagen perfecta para tu publicación: formato cuadrado, estética cálida de la iglesia, sin texto encima. Tarda 30-60 segundos.
                </p>
                <div className="mt-6 grid gap-5 sm:grid-cols-[1fr_auto] sm:items-end">
                  <input
                    value={imgPrompt}
                    onChange={(e) => setImgPrompt(e.target.value)}
                    placeholder="Describe la imagen (vacío = usa tu idea de arriba)"
                    className={inputCls}
                    data-testid="panel-image-prompt"
                  />
                  <button
                    type="button"
                    onClick={generateImage}
                    disabled={genImgBusy}
                    data-testid="panel-image-generate"
                    className="flex items-center justify-center gap-2 bg-fire px-7 py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60"
                  >
                    <Sparkle size={14} weight="light" /> {genImgBusy ? "Generando..." : "Generar imagen"}
                  </button>
                </div>
                {genImgBusy && (
                  <p className="mt-5 text-sm font-light italic text-smoke" data-testid="panel-image-loading">
                    Creando tu imagen... esto puede tardar hasta un minuto.
                  </p>
                )}
                {genImage && (
                  <div className="mt-6 grid gap-6 sm:grid-cols-[280px_1fr]" data-testid="panel-image-result">
                    <img src={`${BACKEND_URL}${genImage.url}`} alt={genImage.prompt} className="aspect-square w-full border border-white/10 object-cover" data-testid="panel-image-preview" />
                    <div className="flex flex-col justify-center">
                      <p className="text-[10px] font-light uppercase tracking-[0.25em] text-smoke">Imagen generada</p>
                      <p className="mt-2 text-sm font-light leading-relaxed text-paper/80">«{genImage.prompt}»</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        <a
                          href={`${BACKEND_URL}${genImage.url}`}
                          download="post-viento-de-gracia.png"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="panel-image-download"
                          className="flex items-center gap-2 bg-paper px-6 py-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-fire hover:text-paper"
                        >
                          <DownloadSimple size={14} weight="light" /> Descargar
                        </a>
                        <button
                          type="button"
                          onClick={publishImageToGallery}
                          disabled={publishingImg}
                          data-testid="panel-image-publish-gallery"
                          className="flex items-center gap-2 border border-fire/50 px-6 py-2.5 text-[10px] font-light uppercase tracking-[0.2em] text-fire transition-colors hover:bg-fire hover:text-ink disabled:opacity-60"
                        >
                          <ImageIcon size={14} weight="light" /> {publishingImg ? "Publicando..." : "Publicar en galería"}
                        </button>
                        <button
                          type="button"
                          onClick={generateImage}
                          data-testid="panel-image-regenerate"
                          className="border border-white/15 px-6 py-2.5 text-[10px] font-light uppercase tracking-[0.2em] text-smoke transition-colors hover:border-fire hover:text-fire"
                        >
                          Generar otra
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              {genContent && (
                <div className="mt-8 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2" data-testid="panel-content-results">
                  {Object.entries(genContent).map(([red, campos]) => {
                    const meta = NET_META[red];
                    if (!meta || typeof campos !== "object") return null;
                    const fullText = Object.entries(campos).map(([k, v]) => `${(FIELD_LABELS[k] || k).toUpperCase()}:\n${v}`).join("\n\n");
                    return (
                      <article key={red} className="flex flex-col bg-surface p-7" data-testid={`panel-content-${red}`}>
                        <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                          <span className="flex h-10 w-10 items-center justify-center border border-white/15 text-fire">
                            <meta.icon size={18} weight="light" />
                          </span>
                          <p className="font-serif text-xl">{meta.name}</p>
                        </div>
                        <div className="flex-1 space-y-4 py-5">
                          {Object.entries(campos).map(([k, v]) => (
                            <div key={k}>
                              <p className="text-[9px] font-light uppercase tracking-[0.25em] text-smoke">{FIELD_LABELS[k] || k}</p>
                              <p className="mt-1 whitespace-pre-wrap text-sm font-light leading-relaxed text-paper/85">{String(v)}</p>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 border-t border-white/10 pt-4">
                          <button onClick={() => copyText(fullText)} data-testid={`panel-content-copy-${red}`} className="flex flex-1 items-center justify-center gap-2 bg-paper py-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-fire hover:text-paper">
                            <Copy size={14} weight="light" /> Copiar
                          </button>
                          <a href={meta.open} target="_blank" rel="noopener noreferrer" data-testid={`panel-content-open-${red}`} className="flex flex-1 items-center justify-center gap-2 border border-white/15 py-2.5 text-[10px] font-light uppercase tracking-[0.2em] text-paper/70 transition-colors hover:border-fire hover:text-fire">
                            <ArrowSquareOut size={14} weight="light" /> Abrir {meta.name}
                          </a>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {history.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-serif text-2xl" data-testid="panel-content-history-title">Historial</h2>
                  <div className="mt-4 space-y-px border border-white/10 bg-white/10" data-testid="panel-content-history">
                    {history.map((h) => (
                      <div key={h.id} className="flex flex-wrap items-center gap-4 bg-surface px-6 py-4" data-testid={`panel-history-${h.id}`}>
                        <p className="min-w-0 flex-1 truncate text-sm font-light text-paper/85">{h.idea}</p>
                        <span className="text-[9px] font-light uppercase tracking-[0.2em] text-smoke">{TIPOS.find((t) => t.v === h.tipo)?.l || h.tipo} · {h.created_at.slice(0, 10)}</span>
                        <button onClick={() => setGenContent(h.resultado)} data-testid={`panel-history-view-${h.id}`} className="border border-white/15 px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-paper hover:text-paper">
                          Ver
                        </button>
                        <button onClick={() => removeContent(h.id)} data-testid={`panel-history-delete-${h.id}`} className="text-smoke transition-colors hover:text-red-400">
                          <Trash size={15} weight="light" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "redes" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-redes-title">Redes sociales</h1>
              <p className="mt-2 text-sm font-light text-smoke">
                Pega el link completo de cada cuenta y el sitio la mostrará al instante en "Predicaciones" y en el pie de página. Las que dejes vacías se verán como "Próximamente".
              </p>

              <Card className="mt-8 p-8" data-testid="panel-redes-form">
                <form onSubmit={saveSocials}>
                  <div className="space-y-7">
                    {[
                      { key: "youtube", name: "YouTube", icon: YoutubeLogo, ph: "https://www.youtube.com/@vientodegracia9235" },
                      { key: "instagram", name: "Instagram", icon: InstagramLogo, ph: "https://www.instagram.com/iglesiavientodegracia" },
                      { key: "facebook", name: "Facebook", icon: FacebookLogo, ph: "https://www.facebook.com/tu-pagina" },
                      { key: "tiktok", name: "TikTok", icon: TiktokLogo, ph: "https://www.tiktok.com/@tu-cuenta" },
                      { key: "spotify", name: "Spotify", icon: SpotifyLogo, ph: "https://open.spotify.com/artist/tu-perfil" },
                    ].map((n) => (
                      <div key={n.key} className="grid items-end gap-3 sm:grid-cols-[auto_1fr_auto]" data-testid={`panel-red-${n.key}`}>
                        <span className="flex h-11 w-11 items-center justify-center border border-white/15 text-paper/70">
                          <n.icon size={20} weight="light" />
                        </span>
                        <div>
                          <p className="mb-1 flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.25em] text-smoke">
                            {n.name}
                            {socials[n.key] ? (
                              <span className="flex items-center gap-1 text-fire"><CheckCircle size={11} weight="fill" /> Activa</span>
                            ) : (
                              <span className="text-smoke/50">Pendiente</span>
                            )}
                          </p>
                          <input
                            value={socials[n.key] || ""}
                            onChange={(e) => setSocials((s) => ({ ...s, [n.key]: e.target.value }))}
                            placeholder={n.ph}
                            className={inputCls}
                            data-testid={`panel-red-input-${n.key}`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => toast.info(`Conexión oficial con ${n.name}: disponible cuando tengas la cuenta — por ahora pega el link aquí y genera el contenido en la pestaña "Contenido IA"`)}
                          data-testid={`panel-red-connect-${n.key}`}
                          className="border border-dashed border-white/20 px-4 py-2 text-[9px] font-light uppercase tracking-[0.15em] text-smoke transition-colors hover:border-fire hover:text-fire"
                        >
                          Conectar
                        </button>
                      </div>
                    ))}
                  </div>
                  <button type="submit" disabled={savingSocials} data-testid="panel-redes-save" className="mt-8 bg-paper px-8 py-3 text-[10px] font-medium uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper disabled:opacity-60">
                    {savingSocials ? "Guardando..." : "Guardar redes"}
                  </button>
                </form>
              </Card>
            </>
          )}

          {tab === "equipo" && (
            <>
              <h1 className="font-serif text-4xl" data-testid="panel-equipo-title">Equipo pastoral</h1>
              <p className="mt-2 text-sm font-light text-smoke">Cada miembro entra con su propio correo y contraseña.</p>
              <Card className="mt-8 p-8" data-testid="panel-users-section">
                <h2 className="flex items-center gap-2 font-serif text-2xl">
                  <UserPlus size={20} weight="light" className="text-fire" /> Agregar miembro
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {users.map((u) => (
                    <span key={u.id} className="flex items-center gap-2 border border-white/15 px-4 py-1.5 text-xs font-light text-paper/70" data-testid={`panel-user-${u.email}`}>
                      {u.nombre} · {u.email}
                      {u.role !== "admin" && session?.role === "admin" && (
                        <button
                          type="button"
                          onClick={() => toggleMusic(u)}
                          data-testid={`panel-user-music-${u.id}`}
                          title="Permiso para subir música a la radio"
                          className={`flex items-center gap-1 border px-2 py-0.5 text-[9px] font-light uppercase tracking-[0.15em] transition-colors ${u.puede_musica ? "border-fire/50 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}
                        >
                          <MusicNote size={11} weight="light" /> {u.puede_musica ? "Radio sí" : "Radio no"}
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                <form onSubmit={addUser} className="mt-6 grid gap-5 sm:grid-cols-4" data-testid="panel-add-user-form">
                  <input required value={newUser.nombre} onChange={(e) => setNewUser((f) => ({ ...f, nombre: e.target.value }))} placeholder="Nombre" className={inputCls} data-testid="panel-newuser-nombre" />
                  <input required type="email" value={newUser.email} onChange={(e) => setNewUser((f) => ({ ...f, email: e.target.value }))} placeholder="Correo" className={inputCls} data-testid="panel-newuser-email" />
                  <input required type="password" minLength={6} value={newUser.password} onChange={(e) => setNewUser((f) => ({ ...f, password: e.target.value }))} placeholder="Contraseña" className={inputCls} data-testid="panel-newuser-password" />
                  <button type="submit" data-testid="panel-newuser-submit" className="bg-paper py-3 text-[10px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper">
                    Agregar
                  </button>
                </form>
              </Card>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
